// Module 3 — Four Walls
// ./index.ts owns this module's key and display name; this file owns its
// action items.

// Libs
import { useParams } from 'react-router';

// RDS
import Heading from '@ramsey-design-system/heading';
import Text from '@ramsey-design-system/text';
import { VideoOutlinedIcon } from '@ramsey-design-system/icons';

// Components
import DiscussionQuestions, {
  DiscussionQuestion
} from '../DiscussionQuestions';
import HLSVideoPlayer from '~fpu/shared/components/HLSVideoPlayer';
import QrCodePrompt from '../QrCodePrompt';

// Utils
import { PATHS } from '~fpu/config/constants';
import storeConfig from '~fpu/config/storeConfig';

// Types
import { WorkshopActionItemDefinition } from '../../types';

function ListYourFourWalls() {
  return (
    <HLSVideoPlayer
      src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/four-walls.mp4"
      thumbnail="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/four-walls.jpg"
    />
  );
}

function TheFourWalls() {
  return (
    <div className="flex flex-col gap-2">
      <Text className="text-4 leading-lg">
        The Four Walls are the four expenses you pay before anything else —
        credit cards, subscriptions, even debt minimums. They keep your family
        fed, housed, and able to get to work.
      </Text>

      <ul className="text-4 leading-lg ml-2 flex flex-col gap-2">
        <li>
          <span className="font-semibold">Food</span> — Groceries and basic
          meals, not restaurants
        </li>
        <li>
          <span className="font-semibold">Utilities</span> — Electric, gas,
          water, and phone
        </li>
        <li>
          <span className="font-semibold">Shelter</span> — Rent or mortgage
          payment
        </li>
        <li>
          <span className="font-semibold">Transportation</span> — Gas, car
          payment, and auto insurance to get to work.
        </li>
      </ul>

      <Text className="text-4 leading-lg">
        If money runs short, cover these four in full before making any other
        payment.
      </Text>
    </div>
  );
}

function TheFourWallsActivity() {
  const { workshopId } = useParams() as { workshopId: string };
  const activityUrl = `${storeConfig.appUrl}${PATHS.MEMBER_DASHBOARD.BUDGET_EXERCISE(workshopId)}`;

  return (
    <QrCodePrompt
      activityUrl={activityUrl}
      slug="four-walls-activity"
      dialogTitle="Four Walls Activity QR Code"
      linkLabel="Budget Activity"
    >
      <Text className="text-5 text-blue-90 leading-lg font-bold">
        Scan the QR code to add your Four Walls to your budget!
      </Text>
      <Text className="text-4 leading-lg">
        For this activity, focus on listing your Four Walls expenses. Don&apos;t
        worry if you don&apos;t know the exact amounts; your best guess is a
        great place to start.
      </Text>
      <div className="border-gray-20 mt-1 overflow-hidden rounded-[20px] border bg-white p-5">
        <img
          src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/four-walls-animation.gif"
          alt="Step 2 of 5, List Your Four Walls"
          className="h-auto w-full [clip-path:inset(0.25%)]"
        />
      </div>
    </QrCodePrompt>
  );
}

function MoneySavingTips() {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <Heading level="3" size="small" className="mb-1">
          Food
        </Heading>
        <ul className="text-4 leading-lg list-disc pl-5">
          <li>
            Shop at cheaper stores (ALDI, Walmart, Costco, Trader Joe&apos;s)
          </li>
          <li>
            Choose budget staples like rice and beans over expensive cuts of
            meat
          </li>
          <li>Only buy meat when it&apos;s on sale</li>
          <li>Cut wants — skip the cookies, kombucha, and pre-cut produce</li>
          <li>Use coupons only for items you already buy</li>
        </ul>
      </div>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Utilities
        </Heading>
        <ul className="text-4 leading-lg list-disc pl-5">
          <li>Only run dishwasher and washing machine with full loads</li>
          <li>
            Program your thermostat — adjusting it 7–10° for 8 hours a day saves
            up to 10% annually
          </li>
          <li>Turn off lights and use less energy overall</li>
        </ul>
      </div>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Housing
        </Heading>
        <ul className="text-4 leading-lg list-disc pl-5">
          <li>Get a roommate to split costs</li>
          <li>Sign a longer lease to lock in your rate</li>
          <li>Consider moving to a more affordable area</li>
        </ul>
      </div>

      <div>
        <Heading level="3" size="small" className="mb-1">
          Transportation
        </Heading>
        <ul className="text-4 leading-lg list-disc pl-5">
          <li>Join free gas rewards programs (Kroger, Costco)</li>
          <li>Use GasBuddy or Waze to find cheap gas nearby</li>
          <li>Combine errands into one trip</li>
          <li>Carpool</li>
        </ul>
      </div>
    </div>
  );
}

const DISCUSSION_QUESTIONS: DiscussionQuestion[] = [
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-1.png',
    text: 'Which of the Four Walls costs you the most each month — and does that surprise you?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-2.png',
    text: 'Have you ever had a month where you struggled to cover one of the Four Walls? What did you do?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-3.png',
    text: 'Is there one wall where you think you could realistically cut back right now?'
  }
];

function Discussion() {
  return <DiscussionQuestions questions={DISCUSSION_QUESTIONS} />;
}

const actionItems: WorkshopActionItemDefinition[] = [
  {
    title: 'List Your Four Walls',
    Content: ListYourFourWalls,
    icon: VideoOutlinedIcon,
    duration: '2:47'
  },
  { title: 'The Four Walls', Content: TheFourWalls },
  { title: 'The Four Walls Activity', Content: TheFourWallsActivity },
  { title: 'Money Saving Tips', Content: MoneySavingTips },
  { title: 'Discussion', Content: Discussion }
];

export default actionItems;
